if (!customElements.get('personalized-recommendations')) {
  class PersonalizedRecommendations extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-recommendation-grid]');
      this.cards = Array.from(this.querySelectorAll('[data-recommendation-product]'));
      this.buttons = Array.from(this.querySelectorAll('[data-recommendation-gender]'));
      this.status = this.querySelector('[data-recommendation-status]');
      this.loadMoreTrigger = this.querySelector('[data-recommendation-load-more]');
      this.storageKey = 'inspecial:recommendations:v1';
      this.genderKey = 'inspecial:recommendation-gender';
      this.seenImpressions = new Set();
      this.hasRemoteCtr = this.cards.some((card) => card.dataset.remoteCtr !== '');
      this.initialLimit = Math.max(1, Number(this.dataset.initialLimit) || 50);
      this.batchSize = Math.max(1, Number(this.dataset.batchSize) || 20);
      this.visibleLimit = this.initialLimit;
      this.eligibleCards = [];

      if (!this.grid || this.cards.length === 0) return;

      this.handleClick = this.handleClick.bind(this);
      this.addEventListener('click', this.handleClick);

      const customerGender = this.dataset.customerGender;
      const savedGender = this.readStorage(this.genderKey);
      const initialGender = ['male', 'female'].includes(customerGender)
        ? customerGender
        : ['all', 'male', 'female'].includes(savedGender)
          ? savedGender
          : 'all';

      this.applyGender(initialGender);
      this.observeImpressions();
      this.observeLoadMore();
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.observer?.disconnect();
      this.loadMoreObserver?.disconnect();
    }

    readStorage(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    }

    writeStorage(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        // Recommendations remain functional when storage is unavailable.
      }
    }

    readStats() {
      try {
        return JSON.parse(this.readStorage(this.storageKey) || '{}');
      } catch (error) {
        return {};
      }
    }

    writeStats(stats) {
      this.writeStorage(this.storageKey, JSON.stringify(stats));
    }

    isEligible(card, gender) {
      if (gender === 'all') return true;

      const handles = (card.dataset.collections || '').split(',').filter(Boolean);
      const eligibleHandles = (this.dataset[gender + 'Collections'] || '').split(',').filter(Boolean);
      return eligibleHandles.some((handle) => handles.includes(handle));
    }

    getCtr(card, stats) {
      if (this.hasRemoteCtr) return Number(card.dataset.remoteCtr) || 0;

      const productStats = stats[card.dataset.productId] || {};
      const impressions = Number(productStats.impressions) || 0;
      const clicks = Number(productStats.clicks) || 0;
      return impressions > 0 ? clicks / impressions : 0;
    }

    applyGender(gender) {
      const stats = this.readStats();
      const eligibleCards = [];

      this.cards.forEach((card) => {
        const eligible = this.isEligible(card, gender);
        card.hidden = !eligible;
        if (eligible) eligibleCards.push(card);
      });

      eligibleCards.sort((left, right) => {
        const ctrDifference = this.getCtr(right, stats) - this.getCtr(left, stats);
        if (ctrDifference !== 0) return ctrDifference;

        const leftClicks = Number(stats[left.dataset.productId]?.clicks) || 0;
        const rightClicks = Number(stats[right.dataset.productId]?.clicks) || 0;
        if (rightClicks !== leftClicks) return rightClicks - leftClicks;

        return Number(left.dataset.originalIndex) - Number(right.dataset.originalIndex);
      });

      eligibleCards.forEach((card) => this.grid.appendChild(card));

      this.buttons.forEach((button) => {
        const selected = button.dataset.recommendationGender === gender;
        button.classList.toggle('button--primary', selected);
        button.classList.toggle('button--secondary', !selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      this.currentGender = gender;
      this.eligibleCards = eligibleCards;
      this.visibleLimit = this.initialLimit;
      this.writeStorage(this.genderKey, gender);
      this.updateVisibleCards();
    }

    updateVisibleCards() {
      this.eligibleCards.forEach((card, index) => {
        card.hidden = index >= this.visibleLimit;
      });

      const shownCount = Math.min(this.visibleLimit, this.eligibleCards.length);
      const hasMore = shownCount < this.eligibleCards.length;

      if (this.loadMoreTrigger) {
        this.loadMoreTrigger.hidden = !hasMore;
      }

      if (this.status) {
        const label = this.buttons.find((button) => button.dataset.recommendationGender === this.currentGender)?.innerText.trim() || this.currentGender;
        this.status.textContent = `${shownCount} of ${this.eligibleCards.length} products shown for ${label}`;
      }
    }

    loadMore() {
      if (this.visibleLimit >= this.eligibleCards.length) return;

      this.visibleLimit += this.batchSize;
      this.updateVisibleCards();
      this.refreshObserver();
    }

    handleClick(event) {
      const genderButton = event.target.closest('[data-recommendation-gender]');
      if (genderButton) {
        this.applyGender(genderButton.dataset.recommendationGender);
        this.refreshObserver();
        return;
      }

      const card = event.target.closest('[data-recommendation-product]');
      if (!card || card.hidden) return;

      const productAction = event.target.closest('a[href*="/products/"], .quick-view__button');
      if (!productAction) return;

      const stats = this.readStats();
      const productId = card.dataset.productId;
      const productStats = stats[productId] || { impressions: 0, clicks: 0 };
      productStats.clicks = (Number(productStats.clicks) || 0) + 1;
      stats[productId] = productStats;
      this.writeStats(stats);
      this.publish('inspecial_recommendation_clicked', card);
    }

    observeImpressions() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        const stats = this.readStats();
        let changed = false;

        entries.forEach((entry) => {
          const card = entry.target;
          const productId = card.dataset.productId;
          if (!entry.isIntersecting || card.hidden || this.seenImpressions.has(productId)) return;

          const productStats = stats[productId] || { impressions: 0, clicks: 0 };
          productStats.impressions = (Number(productStats.impressions) || 0) + 1;
          stats[productId] = productStats;
          this.seenImpressions.add(productId);
          changed = true;
          this.publish('inspecial_recommendation_viewed', card);
        });

        if (changed) this.writeStats(stats);
      }, { threshold: 0.5 });

      this.refreshObserver();
    }

    observeLoadMore() {
      if (!this.loadMoreTrigger || !('IntersectionObserver' in window)) return;

      this.loadMoreObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadMore();
      }, { rootMargin: '600px 0px' });

      this.loadMoreObserver.observe(this.loadMoreTrigger);
    }

    refreshObserver() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.cards.filter((card) => !card.hidden).forEach((card) => this.observer.observe(card));
    }

    publish(eventName, card) {
      if (!window.Shopify?.analytics?.publish) return;

      window.Shopify.analytics.publish(eventName, {
        productId: card.dataset.productId,
        productTitle: card.dataset.productTitle,
        genderPreference: this.currentGender,
        collectionHandles: (card.dataset.collections || '').split(',').filter(Boolean)
      });
    }
  }

  customElements.define('personalized-recommendations', PersonalizedRecommendations);
}
